import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, CircularProgress } from '@mui/material';
import * as Tone from 'tone';
import { useTranslation } from 'react-i18next';

const PlaybackMetronome = ({ tempo, soundUrl, accentUrl, beatsPerMeasure, accent, isMobile }) => {
  const { t } = useTranslation();

  const [isPlaying, setIsPlaying] = useState(false);
  const [bpmTempo, setBpmTempo] = useState(tempo);
  const [currentBeat, setCurrentBeat] = useState(1);
  const [clickSound] = useState(new Tone.Player(soundUrl).toDestination());
  const [accentSound] = useState(new Tone.Player(accentUrl).toDestination());

  useEffect(() => {
    if (soundUrl) {
      clickSound.load(soundUrl);
    }
    if (accentUrl) {
      accentSound.load(accentUrl);
    }
  }, [soundUrl, accentUrl])
  

  useEffect(() => {
    Tone.getTransport().bpm.value = tempo;
    Tone.getTransport().timeSignature = [beatsPerMeasure, 4];
    Tone.getTransport().stop();
    Tone.getTransport().cancel();

    return () => {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
    };
  }, [tempo, beatsPerMeasure]);

  const startMetronome = async () => {
    if (isPlaying) return;

    setIsPlaying(true);
    await Tone.start();

    const transport = Tone.getTransport();
    transport.cancel();

    const startTime = transport.seconds;
  

    transport.scheduleRepeat((time) => {
      const beat = Math.ceil((Tone.getTransport().seconds * tempo) / 60) % beatsPerMeasure + 1;

      if (accent && beat === 1) {
        accentSound.start(time);
      } else {
        clickSound.start(time);
      }

      setCurrentBeat(beat);
    }, '4n', startTime);

    Tone.getTransport().start();
  };

  const stopMetronome = () => {
    setIsPlaying(false);
    setCurrentBeat(1);
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
  };

  useEffect(() => {
    stopMetronome();
  }, [tempo, soundUrl, accentUrl, beatsPerMeasure, accent])
  

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 3,
        maxWidth: 300,
        margin: 'auto',
        borderRadius: 2,
        boxShadow: 4,
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)), url('/logo512.png')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      <Typography 
          sx={{
            fontWeight: 'bold',
            color: '#4A90E2',
            textAlign: 'center',
            lineHeight: '1.2',
          }}
          variant="h5" 
          gutterBottom
        >
        {t('metronome_title')}
       <Typography 
          sx={{
            fontWeight: 'bold',
            color: '#4A90E2',
            textAlign: 'center',
            lineHeight: '1.2',
            marginBottom: 3
          }} 
          variant="body2"
        >
          {tempo} {t('bpm')}
       </Typography>
      </Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          color='info'
          variant="determinate"
          value={(currentBeat / beatsPerMeasure) * 100}
          size={110}
        />
        <Box
          sx={{
            top: 0,
            left: 1,
            bottom: -135,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography  sx={{
            fontWeight: 'bold',
          }} variant="h6" component="div" color="textSecondary">
            {currentBeat}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={startMetronome}
          disabled={isPlaying}
        >
          {t('play')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={stopMetronome}
          disabled={!isPlaying}
        >
          {t('stop')}
        </Button>
      </Box>
    </Box>
  );
};

export default PlaybackMetronome;
